import { z } from 'zod';
import { PRICE_RULE_STATUSES, PRICE_RULE_TYPES, SERVICE_TYPES, VEHICLE_TYPES } from '../constants.js';
import { currencySchema, idSchema, isoDateTime, pageQuerySchema, paginated, moneySchema } from './common.js';
export const priceRuleTypeSchema = z.enum(PRICE_RULE_TYPES);
export const priceRuleStatusSchema = z.enum(PRICE_RULE_STATUSES);
/**
 * Versioned price rule (PRD): by client, route or zone, service type,
 * weight band, vehicle type or fixed rate — with effective dates and tax.
 */
export const priceRuleSchema = z.object({
    id: idSchema,
    name: z.string().min(1).max(160),
    /** null = applies to all clients (specificity ordering handled in shared/pricing). */
    clientId: idSchema.nullish(),
    zoneId: idSchema.nullish(),
    routeId: idSchema.nullish(),
    serviceType: z.enum(SERVICE_TYPES).nullish(),
    vehicleType: z.enum(VEHICLE_TYPES).nullish(),
    weightMinKg: z.number().finite().min(0).nullish(),
    weightMaxKg: z.number().finite().positive().nullish(),
    ruleType: priceRuleTypeSchema,
    fixedAmount: moneySchema.nullish(),
    ratePerKg: moneySchema.nullish(),
    currency: currencySchema.default('GBP'),
    taxRatePct: z.number().finite().min(0).max(100).default(20),
    version: z.number().int().min(1),
    status: priceRuleStatusSchema,
    effectiveFrom: isoDateTime,
    effectiveTo: isoDateTime.nullish(),
    createdBy: idSchema.nullish(),
    createdAt: isoDateTime,
    updatedAt: isoDateTime,
});
export const priceRuleCreateSchema = z
    .object({
    name: z.string().trim().min(1).max(160),
    clientId: idSchema.nullish(),
    zoneId: idSchema.nullish(),
    routeId: idSchema.nullish(),
    serviceType: z.enum(SERVICE_TYPES).nullish(),
    vehicleType: z.enum(VEHICLE_TYPES).nullish(),
    weightMinKg: z.number().finite().min(0).nullish(),
    weightMaxKg: z.number().finite().positive().nullish(),
    ruleType: priceRuleTypeSchema,
    fixedAmount: moneySchema.nullish(),
    ratePerKg: moneySchema.nullish(),
    currency: currencySchema.default('GBP'),
    taxRatePct: z.number().finite().min(0).max(100).default(20),
    status: priceRuleStatusSchema.default('active'),
    effectiveFrom: isoDateTime,
    effectiveTo: isoDateTime.nullish(),
    /** Required reason when creating an override-priced rule (audited). */
    overrideReason: z.string().max(1000).nullish(),
})
    .refine((v) => v.ruleType !== 'fixed' || v.fixedAmount != null, {
    message: 'fixedAmount is required for fixed rules',
    path: ['fixedAmount'],
})
    .refine((v) => v.ruleType !== 'per_kg' || v.ratePerKg != null, {
    message: 'ratePerKg is required for per_kg rules',
    path: ['ratePerKg'],
})
    .refine((v) => v.weightMinKg == null || v.weightMaxKg == null || v.weightMaxKg > v.weightMinKg, {
    message: 'weightMaxKg must be greater than weightMinKg',
    path: ['weightMaxKg'],
});
/**
 * Editing an active rule creates a NEW version (previous version is archived
 * with effectiveTo set). Only these fields may change without a new rule.
 */
export const priceRuleUpdateSchema = z
    .object({
    name: z.string().trim().min(1).max(160).optional(),
    zoneId: idSchema.nullish(),
    routeId: idSchema.nullish(),
    fixedAmount: moneySchema.nullish(),
    ratePerKg: moneySchema.nullish(),
    taxRatePct: z.number().finite().min(0).max(100).optional(),
    status: priceRuleStatusSchema.optional(),
    effectiveFrom: isoDateTime.optional(),
    effectiveTo: isoDateTime.nullish(),
    reason: z.string().trim().min(3).max(1000),
})
    .refine((v) => Object.keys(v).length > 1, { message: 'No fields to update' });
export const priceRuleListSchema = paginated(priceRuleSchema);
export const priceRuleListQuerySchema = pageQuerySchema.extend({
    clientId: idSchema.optional(),
    zoneId: idSchema.optional(),
    routeId: idSchema.optional(),
    status: priceRuleStatusSchema.optional(),
    includeArchived: z.coerce.boolean().default(false),
});
/* -------- preview: which rule + charge would apply to a job? ------------- */
export const pricePreviewSchema = z.object({
    clientId: idSchema.nullish(),
    zoneId: idSchema.nullish(),
    routeId: idSchema.nullish(),
    serviceType: z.enum(SERVICE_TYPES).optional(),
    vehicleType: z.enum(VEHICLE_TYPES).optional(),
    weightKg: z.number().finite().min(0),
    at: isoDateTime.optional(),
});
export const chargeResultSchema = z.object({
    priceRuleId: idSchema,
    priceRuleVersion: z.number().int(),
    currency: currencySchema,
    subtotal: z.number(),
    taxRatePct: z.number(),
    taxAmount: z.number(),
    total: z.number(),
    basis: priceRuleTypeSchema,
    billableKg: z.number(),
});
export const pricePreviewResultSchema = z.object({
    matched: z.boolean(),
    requiresManualPricing: z.boolean(),
    rule: priceRuleSchema.nullable(),
    charge: chargeResultSchema.nullable(),
});
//# sourceMappingURL=pricing.js.map